import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { Box, Button, Divider, Notification, Spinner, StackView, Typography } from '@telus-uds/components-web';
import queryString from 'query-string';

import request, { apiHost } from '../utils/request';
import FieldCheckbox from '../form/fields/fieldCheckbox';
import ValidateLoader from '../UI/validateLoader';
import Loading from './loading';

import { ContentWrapper, SignatureContainer } from '../design/css';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';

const SignContract = ({ location: { search } }) => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    contractSignature: {
      header,
      consetText,
      errorMessage,
      submitErrorMessage,
      errorCheck,
      acceptButton,
      loadingMessage,
      submittingMessage,
      signatureText,
      clearText
    }
  } = localizedStrings;
  const history = useHistory();
  const [orderId, setOrderId] = useState();
  const [signature, setSignature] = useState(false);
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const signCanvas = useRef();
  const { token, lang } = queryString.parse(search);
  const getContractUrl = id =>
    `${apiHost}/orders/${id}/contract-pdf?lang=${lang}`;

  useEffect(() => {
    const checkToken = async () => {
      const { data } = await request.get(
        `/orders/validate-token/?token=${token}`
      );

      if (data.success) {
        setOrderId(data.id);
      } else {
        history.push('/sign-contract/expired');
      }
    };
    checkToken();
  }, []);

  const captureSignature = (isConfirmed = false) => {
    if (!isConfirmed && signCanvas.current.isEmpty()) {
      setError(errorMessage);
      return;
    }
    if (!isConfirmed && !isChecked) {
      setError(errorCheck);
      return;
    }

    const signContract = async confirmed => {
      setError(false);
      setSubmitting(true);
      const payload = { token, lang, signature, isStatusChanged: confirmed };
      try {
        const res = await request.post('/orders/contract-signed', payload);
        if (res.data.success) {
          setShowPreview(true);
          setIsChecked(false);
          if (confirmed) history.push('/sign-contract/success');
        } else {
          history.push('/sign-contract/expired');
        }
      } catch (err) {
        // The backend may have already applied the update even if this
        // request errored/timed out client-side, so don't leave the user
        // stuck on the spinner - surface an error instead.
        setError(submitErrorMessage);
      } finally {
        setSubmitting(false);
      }
    };

    signContract(isConfirmed);
  };

  const clearSignature = () => {
    if (!signCanvas.current.isEmpty()) {
      signCanvas.current.clear();
      setSignature(null);
    }
  };

  const onChangeOfSignaturePad = () => {
    setError(false);
    setSignature(signCanvas.current.getTrimmedCanvas().toDataURL());
  };

  const onChange = () => setIsChecked(!isChecked);

  const checkBoxFieldData = {
    checked: isChecked,
    name: 'consent',
    value: 'consent',
    label: consetText,
    onChange
  };

  return !orderId ? (
    <Loading isVisible>
      <ValidateLoader message={loadingMessage} />
    </Loading>
  ) : (
    <>
      {showPreview ? (
        <div className="container 85%">
          <Spinner
            fullScreen={submitting}
            label={submittingMessage}
            show={submitting}
          />
          <ContentWrapper>
            <StackView space={3}>
              <Typography heading="h2" variant={{ size: 'h2' }} block>Confirm</Typography>
              <Notification variant={{ style: 'subtle' }}>
                Please confirm if the signature looks correct. If not then you
                can sign it again. If it looks good then just press Submit.
              </Notification>
              <embed
                src={getContractUrl(orderId)}
                type="application/pdf"
                width="100%"
                height="800px"
              />
              {error && (
                <Notification variant={{ validation: 'error' }}>
                  {error}
                </Notification>
              )}
              <div>
                <Button
                  variant={{ priority: 'low' }}
                  disabled={submitting}
                  onPress={() => setShowPreview(false)}
                >
                  Sign again
                </Button>
                <div style={{ display: 'inline', marginLeft: '1rem' }}>
                  <Button variant={{ priority: 'high' }} disabled={submitting} onPress={() => captureSignature(true)}>Submit</Button>
                </div>
              </div>
            </StackView>
          </ContentWrapper>
        </div>
      ) : (
        <div className="container 85%">
          <Spinner
            fullScreen={submitting}
            label={submittingMessage}
            show={submitting}
          />
          <ContentWrapper>
            <Typography heading="h2" variant={{ size: 'h2' }} block>{header}</Typography>
            <Divider space={2} variant={{ inverse: true }} />
            <embed
              src={getContractUrl(orderId)}
              type="application/pdf"
              width="100%"
              height="600px"
            />
            <Divider space={2} variant={{ inverse: true }} />
            <StackView space={3}>
              <StackView space={1}>
                <FieldCheckbox data={checkBoxFieldData} />
              </StackView>
              <Typography heading="h4" variant={{ size: 'h4' }} block>{signatureText}</Typography>
              <SignatureContainer>
                <SignatureCanvas
                  ref={signCanvas}
                  onEnd={onChangeOfSignaturePad}
                  penColor="green"
                  canvasProps={{
                    width: 400,
                    height: 150,
                    className: 'sigCanvas'
                  }}
                />
                {error && (
                  <Notification variant={{ validation: 'error' }}>
                    {error}
                  </Notification>
                )}
              </SignatureContainer>
              {signature && (
                <div>
                  <Button variant={{ text: true }} onPress={clearSignature}>{clearText}</Button>
                </div>
              )}
              <div>
                <Button variant={{ priority: 'high' }} disabled={submitting} onPress={() => captureSignature(false)}>
                  {acceptButton}
                </Button>
              </div>
            </StackView>
          </ContentWrapper>
        </div>
      )}
    </>
  );
};

SignContract.propTypes = {
  location: PropTypes.objectOf(PropTypes.string).isRequired
};

export default SignContract;
